import { Box, Stack, Typography } from '@mui/material';
import type { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader';
import { useTranslation } from 'react-i18next';

import IconButton from '@src/shared/ui/Button/IconButton';
import { getDateAdapterLocale } from '@src/shared/lib';
import { ArrowLeftIcon, ArrowRightIcon } from '@src/shared/ui/icons';
import { addMonths, format } from 'date-fns';
import styles from './styles';

function CalendarHeader(props: PickersCalendarHeaderProps) {
  const { currentMonth, onMonthChange, minDate, maxDate, view, onViewChange } =
    props;
  const { t } = useTranslation();
  const locale = getDateAdapterLocale();

  const stepMonths = view === 'month' ? 12 : 1;
  const prev = addMonths(currentMonth, -stepMonths);
  const next = addMonths(currentMonth, stepMonths);
  const prevDisabled =
    view === 'month'
      ? Boolean(minDate && prev.getFullYear() < minDate.getFullYear())
      : Boolean(
          minDate &&
            prev < new Date(minDate.getFullYear(), minDate.getMonth(), 1),
        );
  const nextDisabled =
    view === 'month'
      ? Boolean(maxDate && next.getFullYear() > maxDate.getFullYear())
      : Boolean(
          maxDate &&
            next > new Date(maxDate.getFullYear(), maxDate.getMonth(), 1),
        );

  const label =
    view === 'day'
      ? format(currentMonth, 'LLLL yyyy', { locale })
      : format(currentMonth, 'yyyy', { locale });

  const nextView: 'year' | 'month' | 'day' =
    view === 'day' ? 'month' : view === 'month' ? 'year' : 'day';

  const showArrows = view !== 'year';

  return (
    <Stack direction="row" alignItems="center" sx={styles.header}>
      {showArrows ? (
        <IconButton
          size="small"
          disabled={prevDisabled}
          onClick={() => onMonthChange(prev)}
          tooltipTitle={t('common.previous', 'Previous')}
        >
          <ArrowLeftIcon fontSize="small" />
        </IconButton>
      ) : (
        <Box component="span" sx={styles.headerSpacer} />
      )}
      <Typography
        variant="body2"
        onClick={() => onViewChange?.(nextView)}
        sx={styles.headerLabel}
      >
        {label}
      </Typography>
      {showArrows ? (
        <IconButton
          size="small"
          disabled={nextDisabled}
          onClick={() => onMonthChange(next)}
          tooltipTitle={t('common.next', 'Next')}
        >
          <ArrowRightIcon fontSize="small" />
        </IconButton>
      ) : (
        <Box component="span" sx={styles.headerSpacer} />
      )}
    </Stack>
  );
}

export default CalendarHeader;
